import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'

export type ItemRect = { top: number; height: number; left: number; width: number }

function pickNearest(pointX: number, rects: readonly (ItemRect | undefined)[], originX: number) {
  let closestIndex: number | null = null
  let closestDistance = Infinity
  let containingIndex: number | null = null
  rects.forEach((rect, index) => {
    if (!rect) return
    const start = originX + rect.left
    if (pointX >= start && pointX <= start + rect.width) containingIndex = index
    const distance = Math.abs(pointX - (start + rect.width / 2))
    if (distance < closestDistance) {
      closestDistance = distance
      closestIndex = index
    }
  })
  return containingIndex ?? closestIndex
}

export function useFluidHover<T extends HTMLElement>(containerRef: RefObject<T | null>) {
  const itemsRef = useRef(new Map<number, HTMLElement>())
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [itemRects, setItemRects] = useState<ItemRect[]>([])
  const itemRectsRef = useRef<ItemRect[]>([])
  const rafIdRef = useRef<number | null>(null)
  const measureRafIdRef = useRef<number | null>(null)

  const measureItems = useCallback(() => {
    if (!containerRef.current) return
    const rects: ItemRect[] = []
    let everyItemHasLayout = true
    itemsRef.current.forEach((element, index) => {
      if (element.offsetParent === null && element.offsetWidth === 0 && element.offsetHeight === 0) {
        everyItemHasLayout = false
        return
      }
      rects[index] = {
        top: element.offsetTop,
        height: element.offsetHeight,
        left: element.offsetLeft,
        width: element.offsetWidth,
      }
    })
    if (!everyItemHasLayout || JSON.stringify(rects) === JSON.stringify(itemRectsRef.current)) return
    itemRectsRef.current = rects
    setItemRects(rects)
  }, [containerRef])

  const scheduleMeasurement = useCallback(() => {
    if (measureRafIdRef.current !== null) cancelAnimationFrame(measureRafIdRef.current)
    measureRafIdRef.current = requestAnimationFrame(() => {
      measureRafIdRef.current = null
      measureItems()
    })
  }, [measureItems])

  const registerItem = useCallback(
    (index: number, element: HTMLElement | null) => {
      if (element) itemsRef.current.set(index, element)
      else itemsRef.current.delete(index)
      scheduleMeasurement()
    },
    [scheduleMeasurement]
  )

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!(e.currentTarget as Node).contains(e.target as Node)) return
      const pointX = e.clientX
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null
        const container = containerRef.current
        if (!container) return
        const originX = container.getBoundingClientRect().left + container.clientLeft
        setActiveIndex(pickNearest(pointX, itemRectsRef.current, originX))
      })
    },
    [containerRef]
  )

  const onMouseLeave = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
    setActiveIndex(null)
  }, [])

  // Items register during commit, so measuring here puts the highlight in the first paint.
  useLayoutEffect(measureItems, [measureItems])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(scheduleMeasurement)
    observer.observe(container)
    return () => observer.disconnect()
  }, [containerRef, scheduleMeasurement])

  useEffect(
    () => () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current)
      if (measureRafIdRef.current !== null) cancelAnimationFrame(measureRafIdRef.current)
    },
    []
  )

  return { activeIndex, setActiveIndex, itemRects, handlers: { onMouseMove, onMouseLeave }, registerItem }
}
